import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Layout,
  Users,
  Tag,
  BookOpen,
  Clock,
  Menu,
  X,
  ChevronDown,
  ArrowRight
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { useCountdown } from '../hooks/useCountdown';
import { m, AnimatePresence } from 'framer-motion';
import featuredTemplates from '../data/navFeaturedTemplates.json';
import { trackUmamiEvent } from '../utils/umami';

const Navbar = () => {
  const location = useLocation();
  const { formattedTime } = useCountdown(120);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleMenu = () => {
    const nextState = !isMenuOpen;
    trackUmamiEvent('Nav Mobile Menu Toggle', { state: nextState ? 'open' : 'close' });
    setIsMenuOpen(nextState);
  };

  const navLink = (path: string, label: string, Icon: React.ComponentType<{ className?: string }>) => {
    const isActive = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
    return (
      <Link
        to={path}
        onClick={() => trackUmamiEvent('Nav Link Click', { label, target: path })}
        className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
          isActive
            ? 'bg-gray-100 dark:bg-dark-800 text-gray-900 dark:text-white'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800/50 hover:text-gray-900 dark:hover:text-white'
        }`}
      >
        <Icon className="h-4 w-4 shrink-0 opacity-80" />
        {label}
      </Link>
    );
  };

  return (
    <>
      {/* Promo bar */}
      <div className="bg-[#f45984] dark:bg-[#e04a73] py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-xs sm:text-sm text-center text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.2)]">
            <Tag className="h-3.5 w-3.5 text-white/90" />
            <span><span className="font-semibold text-white">Code </span><span className="font-bold text-white">WELCOME10</span><span className="font-semibold text-white"> · -10%</span></span>
            <span className="text-white/70">|</span>
            <span className="inline-flex items-center gap-1.5 text-white font-medium">
              <Clock className="h-3.5 w-3.5" />
              <span className="font-mono text-xs">{formattedTime}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/80 dark:bg-dark-950/80 backdrop-blur-xl border-b border-gray-200/80 dark:border-dark-800/80 shadow-sm'
            : 'bg-white dark:bg-dark-950 border-b border-gray-100 dark:border-dark-800/50'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 sm:h-20 items-center gap-2">
            {/* Logo */}
            <Link
              to="/"
              onClick={() => trackUmamiEvent('Nav Logo Click', { target: '/' })}
              className="flex items-center gap-3 group"
              aria-label="Templates Notion - Accueil"
            >
              <img
                src="/images/favicon/logo-64.webp"
                alt=""
                className="h-8 w-8 rounded-lg transition-transform duration-200 group-hover:scale-105"
                loading="eager"
              />
              <div className="hidden sm:block">
                <span className="font-semibold text-gray-900 dark:text-white block leading-tight">
                  Templates Notion
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Votre organisation simplifiée
                </span>
              </div>
            </Link>

            {/* Desktop nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navLink('/', 'Accueil', Home)}

              {/* Templates dropdown */}
              <div className="relative group">
                <button
                  type="button"
                  onClick={() => trackUmamiEvent('Nav Templates Dropdown Toggle')}
                  className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                    location.pathname === '/templates'
                      ? 'bg-gray-100 dark:bg-dark-800 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800/50 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <Layout className="h-4 w-4 shrink-0 opacity-80" />
                  Templates
                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180" />
                </button>
                <div className="absolute left-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-all duration-200 z-50">
                  <div className="w-80 rounded-2xl border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 shadow-xl overflow-hidden">
                    <div className="p-3 space-y-2">
                      {featuredTemplates.map((template) => (
                        <Link
                          key={template.id}
                          to={`/${template.slug}`}
                          onClick={() => trackUmamiEvent('Nav Template Click', { slug: template.slug })}
                          className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-800 transition-all duration-200 group/item"
                        >
                          <div className="shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-gray-100 dark:bg-dark-800">
                            <picture>
                              {template.images?.thumbnailWebp && (
                                <source srcSet={template.images.thumbnailWebp} type="image/webp" />
                              )}
                              <img
                                src={template.images?.mainThumbnail || template.images?.main}
                                alt=""
                                loading="lazy"
                                className="w-full h-full object-cover group-hover/item:scale-105 transition-transform"
                              />
                            </picture>
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-gray-900 dark:text-white truncate block text-sm">
                              {template.title}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{template.price}</span>
                          </div>
                          <ArrowRight className="h-4 w-4 text-gray-400 shrink-0 group-hover/item:translate-x-0.5 transition-transform" />
                        </Link>
                      ))}
                    </div>
                    <div className="border-t border-gray-100 dark:border-dark-800 p-3">
                      <Link
                        to="/templates"
                        onClick={() => trackUmamiEvent('Nav Templates All Click')}
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-teal-600 dark:bg-teal-500 text-white text-sm font-semibold hover:bg-teal-700 dark:hover:bg-teal-400 transition-colors"
                      >
                        Tous les templates
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {navLink('/experts', 'Experts', Users)}
              {navLink('/blog', 'Blog', BookOpen)}
              <Link
                to="/agents"
                onClick={() => trackUmamiEvent('Nav Link Click', { label: 'Agents', target: '/agents' })}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  location.pathname.startsWith('/agents')
                    ? 'bg-gray-100 dark:bg-dark-800 text-gray-900 dark:text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800/50 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <img src="/images/notion_agents_book.png" alt="" className="h-4 w-4 shrink-0 opacity-90" loading="lazy" />
                Agents
              </Link>
            </div>

            {/* Right: theme + CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <ThemeToggle />
              <Link
                to="/templates"
                onClick={() => trackUmamiEvent('Nav CTA Click', { target: '/templates' })}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold hover:bg-gray-800 dark:hover:bg-gray-100 transition-all duration-200 shadow-sm hover:shadow"
              >
                Voir les templates
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Mobile */}
            <div className="flex lg:hidden items-center gap-4">
              <ThemeToggle />
              <button
                onClick={toggleMenu}
                className="p-2.5 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-800 hover:text-gray-900 dark:hover:text-white transition-all duration-200"
                aria-label="Menu"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <m.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25, ease: [0.25, 0.4, 0.25, 1] }}
              className="lg:hidden border-t border-gray-100 dark:border-dark-800 overflow-hidden"
            >
              <div className="bg-white dark:bg-dark-950 px-6 py-8 space-y-1">
                <Link
                  to="/"
                  onClick={() => { trackUmamiEvent('Nav Link Click', { label: 'Accueil', target: '/' }); toggleMenu(); }}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-colors ${
                    location.pathname === '/'
                      ? 'bg-gray-100 dark:bg-dark-800 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800/50'
                  }`}
                >
                  <Home className="h-5 w-5 shrink-0 opacity-80" />
                  Accueil
                </Link>
                <Link
                  to="/templates"
                  onClick={() => { trackUmamiEvent('Nav Link Click', { label: 'Templates', target: '/templates' }); toggleMenu(); }}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-colors ${
                    location.pathname === '/templates'
                      ? 'bg-gray-100 dark:bg-dark-800 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800/50'
                  }`}
                >
                  <Layout className="h-5 w-5 shrink-0 opacity-80" />
                  Templates
                </Link>
                <Link
                  to="/experts"
                  onClick={() => { trackUmamiEvent('Nav Link Click', { label: 'Experts', target: '/experts' }); toggleMenu(); }}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-colors ${
                    location.pathname === '/experts'
                      ? 'bg-gray-100 dark:bg-dark-800 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800/50'
                  }`}
                >
                  <Users className="h-5 w-5 shrink-0 opacity-80" />
                  Experts Notion
                </Link>
                <Link
                  to="/blog"
                  onClick={() => { trackUmamiEvent('Nav Link Click', { label: 'Blog', target: '/blog' }); toggleMenu(); }}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-colors ${
                    location.pathname === '/blog'
                      ? 'bg-gray-100 dark:bg-dark-800 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800/50'
                  }`}
                >
                  <BookOpen className="h-5 w-5 shrink-0 opacity-80" />
                  Blog
                </Link>
                <Link
                  to="/agents"
                  onClick={() => { trackUmamiEvent('Nav Link Click', { label: 'Agents', target: '/agents' }); toggleMenu(); }}
                  className={`flex items-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-colors ${
                    location.pathname === '/agents'
                      ? 'bg-gray-100 dark:bg-dark-800 text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800/50'
                  }`}
                >
                  <img src="/images/notion_agents_book.png" alt="" className="h-5 w-5 shrink-0 opacity-90" loading="lazy" />
                  Agents
                </Link>

                <Link
                  to="/templates"
                  onClick={() => { trackUmamiEvent('Nav CTA Click', { target: '/templates' }); toggleMenu(); }}
                  className="flex items-center justify-center gap-2 w-full mt-8 py-4 rounded-xl bg-teal-600 dark:bg-teal-500 text-white font-semibold hover:bg-teal-700 dark:hover:bg-teal-400 transition-colors"
                >
                  Voir les templates
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </m.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
};

export default Navbar;
